import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function EmploymentGrowthChart({ data }) {
  const pdfRef = useRef();
  if (!data?.length) return <p>No data available</p>;

  // =========================
  // Processing
  // =========================
  const years = data.map((item) => item.year);

  const processed = data.map((row) => ({
    year: row.year,
    total: parseFloat(row.total_employment_growth_pct) || 0,
    female: parseFloat(row.women_growth_pct) || 0,
    male: parseFloat(row.men_growth_pct) || 0,
  }));

  const highestYear = processed.reduce(
    (max, row) => (row.total > max.total ? row : max),
    processed[0],
  );

  const femaleLeader = processed.reduce(
    (max, row) => (row.female > max.female ? row : max),
    processed[0],
  );

  const maleLeader = processed.reduce(
    (max, row) => (row.male > max.male ? row : max),
    processed[0],
  );

  // =========================
  // Chart Data
  // =========================
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Total Growth (%)",
        data: processed.map((r) => r.total),
        backgroundColor: "#014CB2",
        borderRadius: 6,
      },
      {
        label: "Female Growth (%)",
        data: processed.map((r) => r.female),
        backgroundColor: "#ec4899",
        borderRadius: 6,
      },
      {
        label: "Male Growth (%)",
        data: processed.map((r) => r.male),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment Growth Percentage (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Growth Percentage (%)",
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdf.save("employment_growth_report.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Growth Dashboard
        </h2>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Total Growth</p>
            <p className="text-xl font-bold text-blue-800">
              {highestYear.year} ({highestYear.total.toFixed(1)}%)
            </p>
          </div>

          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Female Growth Peak</p>
            <p className="text-xl font-bold text-pink-700">
              {femaleLeader.year} ({femaleLeader.female.toFixed(1)}%)
            </p>
          </div>

          <div className="p-4 bg-cyan-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Male Growth Peak</p>
            <p className="text-xl font-bold text-cyan-700">
              {maleLeader.year} ({maleLeader.male.toFixed(1)}%)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-center border-collapse">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Total %</th>
                  <th className="p-2">Female %</th>
                  <th className="p-2">Male %</th>
                </tr>
              </thead>
              <tbody>
                {processed.map((row) => (
                  <tr
                    key={row.year}
                    className={`${
                      row.year === highestYear.year
                        ? "bg-blue-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.year}</td>
                    <td className="p-2">{row.total.toFixed(2)}%</td>
                    <td className="p-2">{row.female.toFixed(2)}%</td>
                    <td className="p-2">{row.male.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="min-w-[400px] w-full h-96">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
